import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Modal, Btn, PageLoader } from "@/components/common";
import {
  SCHOOL_COLORS, mod, CASTER_ABILITY, cantripsKnown, spellsKnown, maxSpellLevel, isCaster,
} from "@/lib/dnd";

// Pure, controlled spell-selection list. `value` is an array of spell entries
// (each at least { name, level, school, description }). `onChange(nextArray)`.
export function SpellSelectList({ className, level, abilities, value, onChange }) {
  const { data: all, isLoading } = useQuery({
    queryKey: ["spells", "", -1],
    queryFn: async () => (await api.get("/spells", { params: { search: "", level: -1 } })).data,
  });

  const abilityMod = mod(abilities?.[CASTER_ABILITY[className]] ?? 10);
  const cantripLimit = cantripsKnown(className, level);
  const spellLimit = spellsKnown(className, level, abilityMod);
  const maxLvl = maxSpellLevel(className, level);

  const classSpells = useMemo(() => {
    const list = (all || []).filter((s) => (s.classes || []).includes(className) && s.level <= maxLvl);
    const groups = {};
    list.forEach((s) => { (groups[s.level] ||= []).push(s); });
    return groups;
  }, [all, className, maxLvl]);

  const selectedNames = useMemo(() => new Set((value || []).map((s) => s.name)), [value]);
  const cantripCount = (value || []).filter((s) => s.level === 0).length;
  const leveledCount = (value || []).filter((s) => s.level > 0).length;

  const toggle = (sp) => {
    if (selectedNames.has(sp.name)) {
      onChange((value || []).filter((s) => s.name !== sp.name));
      return;
    }
    if (sp.level === 0 && cantripCount >= cantripLimit) return;
    if (sp.level > 0 && leveledCount >= spellLimit) return;
    const entry = { id: crypto.randomUUID(), name: sp.name, level: sp.level, school: sp.school, description: sp.description, prepared: true };
    onChange([...(value || []), entry]);
  };

  if (isLoading) return <PageLoader />;
  const levels = Object.keys(classSpells).map(Number).sort((a, b) => a - b);

  return (
    <div className="space-y-4" data-testid="spell-picker">
      <div className="flex flex-wrap gap-2 text-xs">
        {cantripLimit > 0 && (
          <span className={`px-2.5 py-1 rounded-full ${cantripCount >= cantripLimit ? "bg-violet-500/25 text-violet-200" : "bg-white/[0.05] text-zinc-400"}`} data-testid="cantrip-counter">
            Cantrips {cantripCount}/{cantripLimit}
          </span>
        )}
        {spellLimit > 0 && (
          <span className={`px-2.5 py-1 rounded-full ${leveledCount >= spellLimit ? "bg-violet-500/25 text-violet-200" : "bg-white/[0.05] text-zinc-400"}`} data-testid="spell-counter">
            Spells {leveledCount}/{spellLimit}
          </span>
        )}
      </div>

      {levels.length === 0 && (
        <p className="text-sm text-zinc-500 glass rounded-2xl p-6 text-center">No SRD spells available for {className} yet.</p>
      )}

      <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
        {levels.map((lvl) => (
          <div key={lvl}>
            <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2 font-medium">
              {lvl === 0 ? "Cantrips" : `Level ${lvl}`}
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {classSpells[lvl].map((sp) => {
                const on = selectedNames.has(sp.name);
                const limited = !on && ((sp.level === 0 && cantripCount >= cantripLimit) || (sp.level > 0 && leveledCount >= spellLimit));
                return (
                  <button
                    key={sp.name}
                    onClick={() => toggle(sp)}
                    disabled={limited}
                    data-testid={`pick-spell-${sp.name.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
                    className={`flex items-start gap-2.5 p-3 rounded-xl border text-left transition ${on ? "border-violet-500/60 bg-violet-500/10" : limited ? "border-white/5 opacity-40 cursor-not-allowed" : "border-white/10 hover:border-white/25"}`}
                  >
                    <span className={`mt-0.5 w-5 h-5 rounded-md shrink-0 flex items-center justify-center border ${on ? "bg-violet-500 border-violet-400 text-white" : "border-zinc-600"}`}>
                      {on && <Check size={13} />}
                    </span>
                    <span className="min-w-0">
                      <span className="font-semibold text-white text-sm flex items-center gap-1.5">{sp.name}</span>
                      <span className="text-[11px]" style={{ color: SCHOOL_COLORS[sp.school] }}>{sp.school}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Modal wrapper used from the Character Sheet / level-up.
export function SpellPickerModal({ open, onClose, character, onConfirm }) {
  const cls = character?.class;
  // local working copy lives in parent via onConfirm; here we keep it controlled by character.spells
  const value = character?.spells || [];
  if (!isCaster(cls)) return null;
  return (
    <Modal open={open} onClose={onClose} title={`${cls} Spells`} size="lg">
      <ManagedPicker className={cls} level={character?.level} abilities={character?.abilities} initial={value} onConfirm={onConfirm} onClose={onClose} />
    </Modal>
  );
}

function ManagedPicker({ className, level, abilities, initial, onConfirm, onClose }) {
  const [working, setWorking] = useState(initial || []);
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-violet-300 text-sm">
        <Sparkles size={16} /> Choose your {className} spells for level {level}.
      </div>
      <SpellSelectList className={className} level={level} abilities={abilities} value={working} onChange={setWorking} />
      <div className="flex gap-3 pt-2 border-t border-white/[0.06]">
        <Btn variant="ghost" className="flex-1" onClick={onClose}>Cancel</Btn>
        <Btn className="flex-1" data-testid="confirm-spells" onClick={() => { onConfirm(working); onClose(); }}>Save Spells</Btn>
      </div>
    </div>
  );
}
